import { useEffect, useState } from 'react';
import { Settings, CheckCircle, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { listIntegrations, saveIntegration, testIntegration, deleteIntegration } from '../services/api';
import type { Integration, IntegrationPlatform } from '../types/v2';
import { AnalystSettingsCard } from '../components/analyst/AnalystSettingsCard';

export function SettingsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading]           = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setIntegrations(await listIntegrations());
    } catch {
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const wpIntegration     = integrations.find((i) => i.platform === 'wordpress');
  const fbIntegration     = integrations.find((i) => i.platform === 'facebook');
  const pulsarIntegration = integrations.find((i) => i.platform === 'pulsar');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="w-5 h-5 text-blue-400" />
        <h2 className="text-lg font-semibold">Impostazioni — Integrazioni</h2>
      </div>

      <IntegrationForm
        platform="wordpress"
        label="WordPress"
        integration={wpIntegration}
        fields={[
          { key: 'url',         label: 'URL sito',             type: 'url',      placeholder: 'https://miosito.it' },
          { key: 'username',    label: 'Username WordPress',   type: 'text',     placeholder: 'admin' },
          { key: 'appPassword', label: 'Application Password', type: 'password', placeholder: 'xxxx xxxx xxxx xxxx xxxx xxxx' },
        ]}
        onSaved={load}
      />

      <IntegrationForm
        platform="facebook"
        label="Facebook"
        integration={fbIntegration}
        fields={[
          { key: 'pageId',      label: 'Page ID',               type: 'text',     placeholder: '123456789' },
          { key: 'accessToken', label: 'Page Access Token',     type: 'password', placeholder: 'EAA...' },
        ]}
        onSaved={load}
      />

      <IntegrationForm
        platform="pulsar"
        label="Pulsar Platform"
        integration={pulsarIntegration}
        fields={[
          { key: 'bearerToken', label: 'Bearer Token', type: 'password', placeholder: 'eyJhbGciOi...' },
        ]}
        onSaved={load}
        helpText="Il token si ottiene dal tuo account manager Pulsar — non è generabile autonomamente da un pannello self-service."
      />
      <AnalystSettingsCard />
    </div>
  );
}

interface FieldDef {
  key:         string;
  label:       string;
  type:        'text' | 'url' | 'password';
  placeholder: string;
}

function IntegrationForm({
  platform, label, integration, fields, onSaved, helpText,
}: {
  platform:    IntegrationPlatform;
  label:       string;
  integration: Integration | undefined;
  fields:      FieldDef[];
  onSaved:     () => void;
  helpText?:   string;
}) {
  const [values, setValues]       = useState<Record<string, string>>({});
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [saveMsg, setSaveMsg]     = useState<{ ok: boolean; text: string } | null>(null);
  const [testMsg, setTestMsg]     = useState<{ ok: boolean; text: string } | null>(null);

  const setValue = (key: string, val: string) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await saveIntegration(platform, values);
      setSaveMsg({ ok: true, text: 'Integrazione salvata' });
      setValues({});
      onSaved();
    } catch (err) {
      setSaveMsg({ ok: false, text: err instanceof Error ? err.message : 'Errore salvataggio' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestMsg(null);
    try {
      const res = await testIntegration(platform);
      setTestMsg({ ok: res.success, text: res.message });
    } catch (err) {
      setTestMsg({ ok: false, text: err instanceof Error ? err.message : 'Test fallito' });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Rimuovere integrazione ${label}?`)) return;
    setDeleting(true);
    try {
      await deleteIntegration(platform);
      onSaved();
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-200">{label}</h3>
        {integration?.has_credentials && (
          <div className="flex items-center gap-3">
            {integration.test_status === 'ok' && (
              <span className="flex items-center gap-1 text-xs text-green-400">
                <CheckCircle className="w-3.5 h-3.5" /> Connesso
              </span>
            )}
            {integration.test_status === 'error' && (
              <span className="flex items-center gap-1 text-xs text-red-400">
                <AlertCircle className="w-3.5 h-3.5" /> Errore
              </span>
            )}
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-1.5 text-gray-600 hover:text-red-400 disabled:opacity-40 transition-colors"
              title="Rimuovi integrazione"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {helpText && <p className="text-xs text-gray-500">{helpText}</p>}

      {integration?.has_credentials ? (
        <p className="text-xs text-gray-500">
          Credenziali configurate · Aggiornato: {new Date(integration.updated_at).toLocaleDateString('it-IT')}
          {integration.last_tested && ` · Testato: ${new Date(integration.last_tested).toLocaleDateString('it-IT')}`}
        </p>
      ) : (
        <p className="text-xs text-yellow-600">Nessuna credenziale configurata.</p>
      )}

      {/* Fields */}
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
            <input
              type={f.type}
              value={values[f.key] ?? ''}
              onChange={(e) => setValue(f.key, e.target.value)}
              placeholder={integration?.has_credentials ? '(lascia vuoto per non modificare)' : f.placeholder}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
        ))}
      </div>

      {saveMsg && (
        <div className={`flex items-center gap-2 text-sm rounded-lg px-4 py-2 ${
          saveMsg.ok ? 'bg-green-900/20 border border-green-800 text-green-400' : 'bg-red-900/20 border border-red-800 text-red-400'
        }`}>
          {saveMsg.ok ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {saveMsg.text}
        </div>
      )}

      {testMsg && (
        <div className={`flex items-center gap-2 text-sm rounded-lg px-4 py-2 ${
          testMsg.ok ? 'bg-green-900/20 border border-green-800 text-green-400' : 'bg-red-900/20 border border-red-800 text-red-400'
        }`}>
          {testMsg.ok ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {testMsg.text}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving || fields.every((f) => !values[f.key])}
          className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvataggio...</> : 'Salva'}
        </button>
        {integration?.has_credentials && (
          <button
            onClick={handleTest}
            disabled={testing}
            className="border border-gray-700 hover:border-gray-600 text-gray-300 hover:text-white text-sm py-2 px-4 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            {testing ? <><Loader2 className="w-4 h-4 animate-spin" /> Test...</> : 'Testa connessione'}
          </button>
        )}
      </div>
    </section>
  );
}
